import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FiPlus, FiCheckCircle, FiXCircle, FiDatabase, FiArrowLeft, FiUsers, FiAlertTriangle } from "react-icons/fi";
import { MdCheck, MdClose } from "react-icons/md";
import styles from "./Subscriptions.module.scss";
import Axios from "_services/caller.service";
import RightDrawer from "components/Rightpanel/RightDrawer";
import SubscriptionDrawerPanel from "./SubscriptionDrawerPanel";
import ManageUser from "../createUserStepper/ManageUser";
import ConfirmLeaveModal from "../createUserStepper/ConfirmLeaveModal";
import { SUBSCRIPTION_STATUSES, getStatusLabel } from "utils/subscriptionStatuses";

const SUB_TYPES = ["standard", "pro" ,"Plus", "Developer"];

const getVariationTagClass = (variation, styles) => {
  switch ((variation || "").toLowerCase()) {
    case "standard":    return styles.variationTagStandard;
    case "pro":          return styles.variationTagPro;
    case "plus":         return styles.variationTagPlus;
    case "developer":    return styles.variationTagDeveloper;
    case "entendre":     return styles.variationTagEntendre;
    case "maitreaudio":  return styles.variationTagMaitreaudio;
    case "ecoutervoir":  return styles.variationTagEcoutervoir;
    default:             return styles.variationTag;
  }
};

const Subscriptions = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [ownerUsersCountMap, setOwnerUsersCountMap] = useState({});
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("recentSub");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);

  const [newSubForms, setNewSubForms] = useState({});

  const [showManageUser, setShowManageUser] = useState(false);
  const [isManageUserDirty, setIsManageUserDirty] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const handleLeaveManageUser = () => {
    if (isManageUserDirty) {
      setShowLeaveConfirm(true);
    } else {
      setShowManageUser(false);
    }
  };

  const handleConfirmLeave = () => {
    setShowLeaveConfirm(false);
    setIsManageUserDirty(false);
    setShowManageUser(false);
  };

  useEffect(() => {
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const res = await Axios.get("/getAllSubscriptionsDetails");
      setUsers(res.data || []);
    } catch (err) {
      console.error(" Erreur lors du chargement des utilisateurs:", err);
    }
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const fetchOwnerUsersCounts = async () => {
      if (!Array.isArray(users) || users.length === 0) {
        setOwnerUsersCountMap({});
        return;
      }

      const countEntries = await Promise.all(
        users.map(async (user) => {
          try {
            const res = await Axios.get(`/getAuthCabineUser/${user.id}`);
            const inviteUsers = res?.data?.data || [];
            return [
              user.id,
              Array.isArray(inviteUsers) ? inviteUsers.length : 0,
            ];
          } catch (err) {
            console.error(
              `Erreur chargement users OwnerTeamPanel pour owner ${user.id}:`,
              err
            );
            return [user.id, 0];
          }
        })
      );

      if (!isCancelled) {
        setOwnerUsersCountMap(Object.fromEntries(countEntries));
      }
    };

    fetchOwnerUsersCounts();

    return () => {
      isCancelled = true;
    };
  }, [users]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const filteredUsers = useMemo(() => {
    const query = search.toLowerCase().trim();

    const res = (users || []).filter((user) => {
      if (!query) return true;

      const inUser =
        (user.display_name || "").toLowerCase().includes(query) ||
        (user.login || "").toLowerCase().includes(query) ||
        (user.email || "").toLowerCase().includes(query) ||
        String(user.id || "").includes(query);

      const inSubs = (user.subscriptions || []).some((sub) => {
        const inSub =
          (sub.name || "").toLowerCase().includes(query) ||
          (sub.variation || "").toLowerCase().includes(query) ||
          String(sub.subid || "").toLowerCase().includes(query) ||
          String(sub.id || "").includes(query);

        const inCabs = (sub.cabines || []).some((cab) =>
          (cab.cabname || "").toLowerCase().includes(query)
        );

        return inSub || inCabs;
      });

      return inUser || inSubs;
    });

    // ===== TRI =====
    // Helper : date la plus récente d'un user
    const getNewestDate = (u) => {
      const dates = (u.subscriptions || []).map((s) => new Date(s.created_at || 0).getTime());
      return dates.length > 0 ? Math.max(...dates) : 0;
    };
    // Helper : date la plus ancienne d'un user
    const getOldestDate = (u) => {
      const dates = (u.subscriptions || []).map((s) => new Date(s.created_at || 0).getTime());
      return dates.length > 0 ? Math.min(...dates) : 0;
    };
    // Helper : plus petit subId d'un user
    const getMinSubId = (u) => {
      const ids = (u.subscriptions || []).map((s) => s.id || 0);
      return ids.length > 0 ? Math.min(...ids) : 0;
    };
    // Helper : nom du premier abonnement
    const getFirstSubName = (u) => {
      const subs = u.subscriptions || [];
      return subs.length > 0 ? (subs[0].name || "") : "";
    };
    // Helper : variation du premier abonnement
    const getFirstSubVariation = (u) => {
      const subs = u.subscriptions || [];
      return subs.length > 0 ? (subs[0].variation || "") : "";
    };

    if (sortBy === "recentSub") {
      // Plus récent d'abord
      res.sort((a, b) => getNewestDate(b) - getNewestDate(a));
    } else if (sortBy === "oldestSub") {
      // Plus ancien d'abord
      res.sort((a, b) => getOldestDate(a) - getOldestDate(b));
    } else if (sortBy === "user") {
      res.sort((a, b) =>
        (a.display_name || "").localeCompare(b.display_name || "")
      );
    } else if (sortBy === "userId") {
      res.sort((a, b) => (a.id || 0) - (b.id || 0));
    } else if (sortBy === "subId") {
      res.sort((a, b) => getMinSubId(a) - getMinSubId(b));
    } else if (sortBy === "subName") {
      res.sort((a, b) =>
        getFirstSubName(a).localeCompare(getFirstSubName(b))
      );
    } else if (sortBy === "subVariation") {
      res.sort((a, b) =>
        getFirstSubVariation(a).localeCompare(getFirstSubVariation(b))
      );
    } else if (sortBy === "subInactive") {
      // Inactifs en premier
      res.sort((a, b) => {
        const aInactive = (a.subscriptions || []).every((s) => Number(s.is_active) === SUBSCRIPTION_STATUSES.INACTIVE);
        const bInactive = (b.subscriptions || []).every((s) => Number(s.is_active) === SUBSCRIPTION_STATUSES.INACTIVE);
        return bInactive - aInactive;
      });
    } else if (sortBy === "subPaymentPending") {
      // Paiement en retard en premier
      res.sort((a, b) => {
        const aPending = (a.subscriptions || []).some((s) => Number(s.is_active) === SUBSCRIPTION_STATUSES.PAYMENT_PENDING);
        const bPending = (b.subscriptions || []).some((s) => Number(s.is_active) === SUBSCRIPTION_STATUSES.PAYMENT_PENDING);
        return bPending - aPending;
      });
    } else if (sortBy === "email") {
      res.sort((a, b) =>
        (a.email || "").localeCompare(b.email || "")
      );
    }

    return res;
  }, [users, search, sortBy]);

  const selectedEntities = useMemo(() => {
    if (!selectedSub) return { user: null, sub: null };

    const user = users.find((u) => u.id === selectedSub.userId) || null;
    const sub =
      (user?.subscriptions || []).find((s) => s.id === selectedSub.subId) ||
      null;

    return { user, sub };
  }, [users, selectedSub]);

  const openNewSubForm = (userId) => {
    setNewSubForms((prev) => ({
      ...prev,
      [userId]: prev[userId] || { subid: "", name: "", variation: "standard" },
    }));
  };

  const closeNewSubForm = (userId) => {
    setNewSubForms((prev) => {
      const copy = { ...prev };
      delete copy[userId];
      return copy;
    });
  };

  const updateNewSubForm = (userId, patch) => {
    setNewSubForms((prev) => ({
      ...prev,
      [userId]: { ...(prev[userId] || {}), ...patch },
    }));
  };

  const handleAddSubscription = async (userId) => {
    const form = newSubForms[userId];
    const subid = String(form?.subid || "").trim();
    const name = (form?.name || "").trim();
    const variation = (form?.variation || "standard").toLowerCase();

    if (!subid) return alert(t("subscription.odoo_required"));
    if (!name) return alert(t("subscription.sub_name_required"));
    if (!SUB_TYPES.includes(variation))
      return alert(t("subscription.invalid_sub_type"));

    try {
      const res = await Axios.post(`/users/${userId}/subscriptions`, {
        subid,
        name,
        variation,
      });

      const createdSub = res?.data?.data || res?.data;

      setUsers((prev) =>
        (prev || []).map((u) =>
          u.id !== userId
            ? u
            : {
                ...u,
                subscriptions: [
                  ...(u.subscriptions || []),
                  createdSub?.id
                    ? { ...createdSub, cabines: createdSub.cabines || [] }
                    : {
                        id: `tmp-sub-${Date.now()}`,
                        subid,
                        name,
                        variation,
                        is_active: 1,
                        cabines: [],
                        users_count: 0,
                      },
                ],
              }
        )
      );

      closeNewSubForm(userId);
    } catch (err) {
      console.error(" Erreur ajout abonnement:", err);
      alert(t("subscription.error_adding"));
    }
  };

  const openSubscriptionDetails = (user, sub) => {
    setSelectedSub({ userId: user.id, subId: sub.id });
    setDrawerOpen(true);
  };

  return (
    <>
      {showLeaveConfirm && (
        <ConfirmLeaveModal
          onConfirm={handleConfirmLeave}
          onCancel={() => setShowLeaveConfirm(false)}
        />
      )}
      {showManageUser ? (
        <div className={styles.manageUserWrapper}>
          <button 
            className={styles.backButton}
            onClick={handleLeaveManageUser}
            type="button"
            title={t("subscription.back_tooltip")}
          >
            <FiArrowLeft /> {t("subscription.back_button")}
          </button>
          <ManageUser onDirtyChange={setIsManageUserDirty} />
        </div>
      ) : (
        <div className={styles.container}>
          <div className={styles.header}>
            <h2>{t("subscription.manage_users_title")}</h2>

            <div className={styles.actions}>
              <div className={styles.searchSort}>
                <input
                  type="text"
                  placeholder={t("subscription.search_placeholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  title={t("subscription.search_tooltip")}
                />

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className={styles.sortSelect}
                >
                  <option value="recentSub">{t("common.most_recent_first")}</option>
                  <option value="oldestSub">{t("common.oldest_first")}</option>
                  <option value="user">{t("subscription.sort_user_name")}</option>
                  <option value="email">{t("subscription.sort_email")}</option>
                  <option value="userId">{t("subscription.sort_user_id")}</option>
                  <option value="subId">{t("subscription.sort_sub_id")}</option>
                  <option value="subInactive">{t("subscription.sort_inactive_first")}</option>
                  <option value="subPaymentPending">{t("subscription.sort_payment_pending")}</option>
                </select>
              </div>
              
              <button 
                className={`${styles.btn} ${styles.btnCreateNew}`}
                onClick={() => setShowManageUser(true)}
                type="button"
              >
                <FiPlus /> {t("subscription.create_new_account")}
              </button>
            </div>
          </div>

        {filteredUsers.length === 0 ? (
          <p>{t("common.no_users_found")}</p>
        ) : (
          <div className={styles.rows}>
            

            {filteredUsers.map((user) => {
              const subs = user.subscriptions || [];
              const rows = subs.length > 0 ? subs : [null];
              const userTitle = `${user.display_name} (${user.login})  ${user.email}`;

              return (
                <React.Fragment key={user.id}>
                  {rows.map((sub, idx) => {
                    const isFirstUserRow = idx === 0;
                    const ownerUsersCount = Number(ownerUsersCountMap[user.id] || 0);

                    return (
                      <div
                        key={sub?.id || `nosub-${user.id}`}
                        className={styles.row}
                        onClick={() => sub && openSubscriptionDetails(user, sub)}
                        style={{ cursor: sub ? 'pointer' : 'default' }}
                      >
                        {/* Combined User + Subscription Info */}
                        <div className={styles.infoCell}>
                          <div className={styles.userSection}>
                            <span className={styles.userName}>{user.display_name}</span>
                            <div className={styles.userMeta}>
                              <span className={styles.login}>@{user.login}</span>
                              <span className={styles.dot}>•</span>
                              <span className={styles.email}>{user.email}</span>
                              <span className={styles.dot}>•</span>
                              <span className={styles.userId}>ID: {user.id}</span>
                            </div>
                          </div>

                          {sub ? (
                            <div className={styles.subSection}>
                              <div className={styles.subLine} title={`${sub.name} (${sub.variation})`}>
                                <span className={styles.subName}>
                                  {sub.name}
                                </span>
                                <span className={getVariationTagClass(sub.variation, styles)}>
                                  {sub.variation}
                                </span>

                                <span className={styles.dot}>•</span>
                                <span className={styles.subid}>
                                  {sub.subid ? `Odoo: ${sub.subid}` : t("subscription.no_odoo")}
                                </span>

                                <span className={styles.dot}>•</span>
                                <span className={styles.subIdNum}>
                                  Sub ID: {sub.id}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <span className={styles.emptySubInline}>{t("subscription.no_subscription")}</span>
                          )}
                        </div>

                        <div className={styles.actionsCell}>
                          <button
                            className={`${styles.btn} ${styles.btnPrimary} has-tooltip`}
                            onClick={(e) => { e.stopPropagation(); openNewSubForm(user.id); }}
                            type="button"
                            aria-label={t("subscription.add_subscription")}
                            style={isFirstUserRow && !newSubForms[user.id] ? {} : { visibility: 'hidden', pointerEvents: 'none' }}
                          >
                            <FiPlus />
                          </button>

                          {sub ? (
                              <span
                                className={styles.userBadge}
                                aria-label={t("subscription.user_count", { count: ownerUsersCount })}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FiUsers />
                                {ownerUsersCount}
                              </span>
                          ) : null}

                          {sub ? (
                              <span
                                className={styles.cabBadge}
                                aria-label={t("subscription.cabine_count", { count: (sub.cabines || []).length })}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FiDatabase />
                                {(sub.cabines || []).length}
                              </span>
                          ) : null}
                                 {/* Status Indicator (read-only) */}
                              <span
                                className={`${styles.statusIndicator} ${
                                  Number(sub.is_active) === SUBSCRIPTION_STATUSES.ACTIVE
                                    ? styles.statusActive
                                    : Number(sub.is_active) === SUBSCRIPTION_STATUSES.PAYMENT_PENDING
                                    ? styles.statusWarning
                                    : styles.statusInactive
                                }`}
                                title={getStatusLabel(Number(sub.is_active))}
                                aria-label={getStatusLabel(Number(sub.is_active))}
                              >
                                {Number(sub.is_active) === SUBSCRIPTION_STATUSES.ACTIVE && <FiCheckCircle />}
                                {Number(sub.is_active) === SUBSCRIPTION_STATUSES.INACTIVE && <FiXCircle />}
                                {Number(sub.is_active) === SUBSCRIPTION_STATUSES.PAYMENT_PENDING && <FiAlertTriangle />}
                              </span>

                        </div>
                      </div>
                    );
                  })}

                  {newSubForms[user.id] && (
                    <div className={styles.newSubRow}>
                      <div className={styles.infoCell} />

                      <div className={styles.subSection}>
                        <div className={styles.editRowSub}>
                          <input
                            id="newOdooId"
                            type="text"
                            placeholder={t("subscription.odoo_placeholder")}
                            value={newSubForms[user.id]?.subid || ""}
                            onChange={(e) => updateNewSubForm(user.id, { subid: e.target.value })}
                          />
                          <input
                            id="newSubName"
                            type="text"
                            placeholder={t("common.name")}
                            value={newSubForms[user.id]?.name || ""}
                            onChange={(e) => updateNewSubForm(user.id, { name: e.target.value })}
                          />
                          <select
                            id="newSubType"
                            value={newSubForms[user.id]?.variation || "standard"}
                            onChange={(e) => updateNewSubForm(user.id, { variation: e.target.value })}
                          >
                            {SUB_TYPES.map((subType) => (
                              <option key={subType} value={subType}>
                                {subType.toUpperCase()}
                              </option>
                            ))}
                          </select>
                          <button
                            className={styles.btnSave}
                            onClick={() => handleAddSubscription(user.id)}
                            title={t("subscription.add_subscription")}
                            type="button"
                          >
                            <MdCheck />
                          </button>
                          <button
                            className={styles.btnCancel}
                            onClick={() => closeNewSubForm(user.id)}
                            title={t("common.cancel")}
                            type="button"
                          >
                            <MdClose />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        <RightDrawer
          isOpen={drawerOpen}
          onClose={closeDrawer}
          title={
            selectedEntities.sub
              ? `${selectedEntities.sub.name || t("subscription.details_title")} (${selectedEntities.sub.variation || "-"})`
              : t("subscription.details_title")
          }
          width="720px"
          overlay="transparent"
        >
          <SubscriptionDrawerPanel
            user={selectedEntities.user}
            sub={selectedEntities.sub}
            onRefresh={fetchSubscriptions}
          />
        </RightDrawer>
      </div>
      )}
    </>
  );
};

export default Subscriptions;